import { useAppConfig } from 'api/appConfig';
import IdPInputCheckbox from 'components/SSOConfigFields/IdPInputCheckbox';
import IdPInputMultiField from 'components/SSOConfigFields/IdPInputMultiField';
import IdPInputSecretField from 'components/SSOConfigFields/IdPInputSecretField';
import IdPInputSingleSelect from 'components/SSOConfigFields/IdPInputSingleSelect';
import IdpInputTextField from 'components/SSOConfigFields/IdpInputTextField';
import {
	GENERIC_IDP_ID,
	IdPCommonInputFieldName,
	IdPOidcInputFieldName,
	IdPSamlInputFieldName,
	IdPXaaInputFieldName,
	Method,
	OidcBaseScopes,
	OidcGrantTypes,
	OidcPrompts,
	XaaJit,
	XaaSignAlgorithms
} from 'consts';
import { useVerifiedDomains } from 'contexts/VerifiedDomainsCtx/VerifiedDomains';
import { useMemo } from 'react';
import { IIdP, IMethodConfigFields } from 'types/api';
import { IContentFieldConfig } from 'types/fields';

export const useFieldsConfigs = () => {
	const { data: appConfig } = useAppConfig();
	const { verifiedDomains } = useVerifiedDomains();

	return useMemo(
		() =>
			({
				// SAML
				[IdPSamlInputFieldName.IdPMetadataUrl]: {
					name: IdPSamlInputFieldName.IdPMetadataUrl,
					methods: [Method.Saml],
					Component: IdpInputTextField
				},
				[IdPSamlInputFieldName.IdPSsoUrl]: {
					name: IdPSamlInputFieldName.IdPSsoUrl,
					methods: [Method.Saml],
					Component: IdpInputTextField
				},
				[IdPSamlInputFieldName.IdPIssuer]: {
					name: IdPSamlInputFieldName.IdPIssuer,
					methods: [Method.Saml],
					Component: IdpInputTextField
				},
				[IdPSamlInputFieldName.IdPCertificate]: {
					name: IdPSamlInputFieldName.IdPCertificate,
					methods: [Method.Saml],
					Component: IdpInputTextField,
					props: {
						lineNum: 10,
						spellCheck: false
					}
				},

				// OIDC
				[IdPOidcInputFieldName.ProviderName]: {
					name: IdPOidcInputFieldName.ProviderName,
					methods: [Method.Oidc],
					Component: IdpInputTextField
				},
				[IdPOidcInputFieldName.ClientId]: {
					name: IdPOidcInputFieldName.ClientId,
					methods: [Method.Oidc],
					Component: IdpInputTextField
				},
				[IdPOidcInputFieldName.ClientSecret]: {
					name: IdPOidcInputFieldName.ClientSecret,
					methods: [Method.Oidc],
					Component: IdPInputSecretField
				},
				[IdPOidcInputFieldName.Scopes]: {
					name: IdPOidcInputFieldName.Scopes,
					methods: [Method.Oidc],
					Component: IdPInputMultiField,
					props: {
						options: Object.values(OidcBaseScopes),
						limitToOptions: false
					}
				},
				[IdPOidcInputFieldName.GrantType]: {
					name: IdPOidcInputFieldName.GrantType,
					methods: [Method.Oidc],
					Component: IdPInputSingleSelect,
					props: {
						options: Object.values(OidcGrantTypes)
					}
				},
				[IdPOidcInputFieldName.Issuer]: {
					name: IdPOidcInputFieldName.Issuer,
					methods: [Method.Oidc],
					Component: IdpInputTextField
				},
				[IdPOidcInputFieldName.AuthorizationEndpoint]: {
					name: IdPOidcInputFieldName.AuthorizationEndpoint,
					methods: [Method.Oidc],
					Component: IdpInputTextField
				},
				[IdPOidcInputFieldName.TokenEndpoint]: {
					name: IdPOidcInputFieldName.TokenEndpoint,
					methods: [Method.Oidc],
					Component: IdpInputTextField
				},
				[IdPOidcInputFieldName.UserInfoEndpoint]: {
					name: IdPOidcInputFieldName.UserInfoEndpoint,
					methods: [Method.Oidc],
					Component: IdpInputTextField
				},
				[IdPOidcInputFieldName.JWKsEndpoint]: {
					name: IdPOidcInputFieldName.JWKsEndpoint,
					methods: [Method.Oidc],
					Component: IdpInputTextField
				},
				[IdPOidcInputFieldName.Prompt]: {
					name: IdPOidcInputFieldName.Prompt,
					methods: [Method.Oidc],
					Component: IdPInputMultiField,
					props: {
						options: [
							{
								label: 'ssoConfig.fields.prompt.none',
								value: OidcPrompts.None
							},
							{
								label: 'ssoConfig.fields.prompt.login',
								value: OidcPrompts.Login
							},
							{
								label: 'ssoConfig.fields.prompt.consent',
								value: OidcPrompts.Consent
							},
							{
								label: 'ssoConfig.fields.prompt.selectAccount',
								value: OidcPrompts.SelectAccount
							}
						],
						limitToOptions: true
					}
				},
				[IdPOidcInputFieldName.UsePkce]: {
					name: IdPOidcInputFieldName.UsePkce,
					methods: [Method.Oidc],
					Component: IdPInputCheckbox,
					props: {
						helperText: intl.tr('ssoConfig.fields.usePkce.helperText')
					},
					disabledWhen: {
						field: IdPOidcInputFieldName.GrantType,
						value: OidcGrantTypes.Implicit
					}
				},

				// XAA (Cross-App Access / ID-JAG)
				[IdPXaaInputFieldName.Issuer]: {
					name: IdPXaaInputFieldName.Issuer,
					methods: [Method.Xaa],
					Component: IdpInputTextField,
					props: {
						placeholder: intl.tr('ssoConfig.fields.xaaIssuer.placeholder')
					}
				},
				[IdPXaaInputFieldName.JwksUri]: {
					name: IdPXaaInputFieldName.JwksUri,
					methods: [Method.Xaa],
					Component: IdpInputTextField,
					props: {
						placeholder: intl.tr('ssoConfig.fields.xaaJwksUri.placeholder')
					}
				},
				[IdPXaaInputFieldName.SignAlgorithm]: {
					name: IdPXaaInputFieldName.SignAlgorithm,
					methods: [Method.Xaa],
					Component: IdPInputSingleSelect,
					props: {
						options: Object.values(XaaSignAlgorithms)
					}
				},
				[IdPXaaInputFieldName.UserInfoUri]: {
					name: IdPXaaInputFieldName.UserInfoUri,
					methods: [Method.Xaa],
					Component: IdpInputTextField,
					props: {
						placeholder: intl.tr('ssoConfig.fields.xaaUserInfoUri.placeholder')
					}
				},
				[IdPXaaInputFieldName.Jit]: {
					name: IdPXaaInputFieldName.Jit,
					methods: [Method.Xaa],
					Component: IdPInputSingleSelect,
					props: {
						options: Object.values(XaaJit)
					}
				},

				// Common
				[IdPCommonInputFieldName.SSODomains]: {
					name: IdPCommonInputFieldName.SSODomains,
					methods: [Method.Saml, Method.Oidc],
					Component: IdPInputMultiField,
					props: {
						options: appConfig?.forceDomainVerification ? verifiedDomains : [],
						disabled: false,
						limitToOptions: !!appConfig?.forceDomainVerification,
						preventChars: ' ',
						placeholder: intl.tr('ssoConfig.fields.ssoDomains.placeholder'),
						noOptionsText: intl.tr(
							'ssoConfig.fields.ssoDomains.verifiedDomainsNoOptionsText'
						)
					}
				}
			}) as Record<string, IContentFieldConfig>,
		[appConfig?.forceDomainVerification, verifiedDomains]
	);
};

export const getFieldDefaultValue = <K extends keyof IMethodConfigFields>(
	fieldName: K,
	idp: IIdP
): IMethodConfigFields[K] => {
	const fieldDefaultValues: IMethodConfigFields = {
		[IdPOidcInputFieldName.ProviderName]:
			idp && idp.id !== GENERIC_IDP_ID ? idp.name : null,
		[IdPOidcInputFieldName.Scopes]: Object.values(OidcBaseScopes),
		[IdPOidcInputFieldName.Prompt]: [],
		[IdPCommonInputFieldName.SSODomains]: [],
		[IdPOidcInputFieldName.GrantType]: OidcGrantTypes.AuthorizationCode,
		// XAA default - also seeded via the synthesized guide, kept here as a fallback.
		[IdPXaaInputFieldName.SignAlgorithm]: XaaSignAlgorithms.RS256
	};

	return fieldDefaultValues[fieldName] ?? null;
};
